import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../context/TranslationContext';
import shippingService from '../services/shipping';
import billingService from '../services/billing';
import apiService from '../services/api';
import checkoutService from '../services/checkout';
import useAddressValidation, { validateAddressPure } from '../hooks/useAddressValidation';
import useShippingConfig from './useShippingConfig';

export default function useCheckout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cartItems, clearCart, refreshCart } = useCart();
  const { validateAddress } = useAddressValidation();
  const { cities: validCities, countries: validCountries } = useShippingConfig();

  const { variantSku, quantity } = location.state || {};

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [billingAddresses, setBillingAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState(null);

  // Address form state
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', is_default: false,
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState(null);

  const [showBillingAddressForm, setShowBillingAddressForm] = useState(false);
  const [billingAddressForm, setBillingAddressForm] = useState({
    full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', company_name: '', vat_number: '', is_default: false,
  });
  const [savingBillingAddress, setSavingBillingAddress] = useState(false);
  const [billingAddressError, setBillingAddressError] = useState(null);
  const [sameAsShippingAddress, setSameAsShippingAddress] = useState(false);

  // Pickup state
  const [locations, setLocations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDate, setPickupDate] = useState('');

  // Order state
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ vat: 0, shipping: 0, total: 0, totalBeforeRounding: 0, roundedTotal: 0, roundOffDiff: 0, couponDiscount: 0, ruleDiscount: 0, shippingFree: false, freeItems: [], vatExclusive: false, netSubtotal: 0, netShipping: 0, vatOnDiscount: 0 });
  const [couponCode, setCouponCode] = useState('');
  const [couponFeedbackKey, setCouponFeedbackKey] = useState('');
  // Track which coupon codes the user has successfully applied during this
  // checkout session so the OrderSummarySection can render them as green
  // "Coupon XYZ applied" badges (mirroring the Cart's already-applied UX).
  const [appliedCouponCodes, setAppliedCouponCodes] = useState([]);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [volumeDiscount, setVolumeDiscount] = useState(0);
  const [appliedBalance, setAppliedBalance] = useState(0);
  const [fulfillmentMethod, setFulfillmentMethod] = useState('delivery');

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pricingAcknowledged, setPricingAcknowledged] = useState(false);
  // Marketing opt-in: pre-checked by default. The flag is currently UI-only
  const [marketingOptIn, setMarketingOptIn] = useState(true);

  const buildPayload = useCallback((overrides = {}) => {
    const finalShippingAddressId = fulfillmentMethod === 'pickup' ? null : selectedAddressId;
    const finalPickupLocationId = fulfillmentMethod === 'pickup' ? pickupLocationId : null;
    const finalPickupTime = fulfillmentMethod === 'pickup' ? pickupTime : null;
    const finalPickupDate = fulfillmentMethod === 'pickup' ? pickupDate : null;

    const base = {
      shippingAddressId: finalShippingAddressId,
      billingAddressId: selectedBillingAddressId,
      fulfillmentMethod,
      pickupLocationId: finalPickupLocationId,
      pickupTime: finalPickupTime,
      pickupDate: finalPickupDate,
      language: t.langCode,
      ...overrides,
    };

    if (variantSku) {
      base.sku = variantSku;
      base.quantity = quantity;
    }

    return base;
  }, [fulfillmentMethod, selectedAddressId, selectedBillingAddressId, pickupLocationId, pickupTime, pickupDate, variantSku, quantity, t.langCode]);

  // Fetch addresses + pickup locations
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    (async () => {
      try {
        const [shipData, billData] = await Promise.all([
          shippingService.getAddresses(),
          billingService.getAddresses()
        ]);
        setAddresses(shipData);
        setBillingAddresses(billData);
        const defaultAddr = shipData.find(a => a.is_default) || shipData[0];
        setSelectedAddressId(defaultAddr ? defaultAddr.id : null);
        const defaultBill = billData.find(a => a.is_default) || billData[0];
        setSelectedBillingAddressId(defaultBill ? defaultBill.id : null);
      } catch (err) {
        setAddressError(err.message);
      }
    })();

    apiService.getPublicPickupLocations()
      .then(res => setLocations(res))
      .catch(err => console.error(err));

    apiService.getUpcomingHolidays()
      .then(res => setHolidays(res))
      .catch(err => console.error(err));
  }, [isAuthenticated, navigate]);

  // Preview checkout
  useEffect(() => {
    (async () => {
      const body = buildPayload();
      try {
        const data = await checkoutService.previewCheckout(body);
        if (data.items) setItems(data.items);
        setSummary({
          vat: data.vat,
          shipping: data.shipping,
          total: data.total,
          totalBeforeRounding: data.totalBeforeRounding,
          roundedTotal: data.roundedTotal,
          roundOffDiff: data.roundOffDiff ?? 0,
          couponDiscount: data.couponDiscount ?? 0,
          ruleDiscount: data.ruleDiscount ?? 0,
          shippingFree: data.shippingFree ?? false,
          freeItems: data.freeItems ?? [],
          vatExclusive: data.vatExclusive ?? false,
          netSubtotal: data.netSubtotal ?? 0,
          netShipping: data.netShipping ?? 0,
          vatOnDiscount: data.vatOnDiscount ?? 0,
        });
        setBillingStatus(data.billingStatus);
        setVolumeDiscount(data.volumeDiscount || 0);
        setAppliedBalance(data.appliedBalance || 0);
      } catch (err) {
        console.error("Preview fetch failed", err);
      }
    })();
  }, [variantSku, quantity, cartItems, fulfillmentMethod, selectedAddressId, pickupLocationId, pickupTime, buildPayload]);

  const toggleSameAsShipping = useCallback((checked) => {
    setSameAsShippingAddress(checked);
    if (checked) {
      const selectedShipping = addresses.find(a => a.id === selectedAddressId);
      if (selectedShipping) {
        setBillingAddressForm(prev => ({
          ...prev,
          full_name: selectedShipping.full_name || '',
          address_line1: selectedShipping.address_line1 || '',
          address_line2: selectedShipping.address_line2 || '',
          city: selectedShipping.city || '',
          postal_code: selectedShipping.postal_code || '',
          country: selectedShipping.country || 'Switzerland',
        }));
        setSelectedBillingAddressId(null);
        setShowBillingAddressForm(true);
      }
    } else {
      setBillingAddressForm(prev => ({
        ...prev,
        full_name: '',
        address_line1: '',
        address_line2: '',
        city: '',
        postal_code: '',
        country: 'Switzerland',
      }));
    }
  }, [addresses, selectedAddressId]);

  // Address handlers
  const handleAddressSelect = (addressData) => {
    setAddressForm(prev => ({ ...prev, ...addressData }));
  };
  const handleBillingAddressSelect = (addressData) => {
    setBillingAddressForm(prev => ({ ...prev, ...addressData }));
  };
  const handleAddressChange = e => {
    const { name, value } = e.target;
    setAddressForm(f => ({ ...f, [name]: value }));
  };

  const isAddressDeliverable = useCallback(
    (addr) => {
      if (!addr) return false;
      return validateAddressPure(
        { country: addr.country, city: addr.city },
        validCountries,
        validCities,
        t,
        'shipping'
      ).ok;
    },
    [validCountries, validCities, t]
  );

  const deliveryUnavailableIds = useMemo(() => {
    const map = {};
    if (fulfillmentMethod === 'delivery') {
      addresses.forEach(a => {
        if (!isAddressDeliverable(a)) map[a.id] = true;
      });
    }
    return map;
  }, [addresses, fulfillmentMethod, isAddressDeliverable]);

  const selectedDeliveryMessage = useMemo(() => {
    if (fulfillmentMethod !== 'delivery' || !selectedAddressId) return null;
    const addr = addresses.find(a => a.id === selectedAddressId);
    if (!addr) return null;
    const result = validateAddressPure(
      { country: addr.country, city: addr.city },
      validCountries,
      validCities,
      t,
      'shipping'
    );
    return result.ok ? null : result.message;
  }, [fulfillmentMethod, selectedAddressId, addresses, validCountries, validCities, t]);

  const handleAddAddress = async e => {
    e.preventDefault();
    const deliveryResult = validateAddress(addressForm);
    setSavingAddress(true);
    setAddressError(null);
    try {
      const created = await shippingService.addAddress(addressForm);
      setAddresses(prev => [created, ...prev]);
      setShowAddressForm(false);
      setAddressForm({ full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', is_default: false });
      if (deliveryResult.ok) {
        setSelectedAddressId(created.id);
      } else {
        setError(deliveryResult.message);
      }
    } catch (err) {
      setAddressError(err.message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleAddBillingAddress = async e => {
    e.preventDefault();
    setSavingBillingAddress(true);
    setBillingAddressError(null);
    try {
      const created = await billingService.addAddress(billingAddressForm);
      setBillingAddresses(prev => [created, ...prev]);
      setSelectedBillingAddressId(created.id);
      setShowBillingAddressForm(false);
      setSameAsShippingAddress(false);
      setBillingAddressForm({ full_name: '', address_line1: '', address_line2: '', city: '', postal_code: '', country: 'Switzerland', company_name: '', vat_number: '', is_default: false });
    } catch (err) {
      setBillingAddressError(err.message);
    } finally {
      setSavingBillingAddress(false);
    }
  };

  // Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode || isApplyingCoupon) return;
    setIsApplyingCoupon(true);
    const enteredCode = couponCode.trim();
    try {
      await apiService.applyCoupon(enteredCode);
      setCouponFeedbackKey('couponApplied');
      setCouponCode('');
      // Append the applied code (deduped, case-insensitive) so the
      // summary can render a "Coupon XYZ applied" chip.
      setAppliedCouponCodes(prev =>
        prev.some(c => c.toLowerCase() === enteredCode.toLowerCase())
          ? prev
          : [...prev, enteredCode]
      );
      await refreshCart();
      if (variantSku) {
        const data = await checkoutService.previewCheckout(buildPayload());
        setSummary({
          vat: data.vat, shipping: data.shipping, total: data.total,
          totalBeforeRounding: data.totalBeforeRounding, roundedTotal: data.roundedTotal,
          roundOffDiff: data.roundOffDiff ?? 0, couponDiscount: data.couponDiscount ?? 0,
          ruleDiscount: data.ruleDiscount ?? 0,
          shippingFree: data.shippingFree ?? false, freeItems: data.freeItems ?? [],
          vatExclusive: data.vatExclusive ?? false,
          netSubtotal: data.netSubtotal ?? 0,
          netShipping: data.netShipping ?? 0,
          vatOnDiscount: data.vatOnDiscount ?? 0,
        });
        setBillingStatus(data.billingStatus);
        setVolumeDiscount(data.volumeDiscount || 0);
        setAppliedBalance(data.appliedBalance || 0);
      }
    } catch (err) {
      if (err.error_code === 'ALREADY_USED') {
        setCouponFeedbackKey('couponAlreadyUsed');
      } else {
        setCouponFeedbackKey('invalidCoupon');
      }
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  // Checkout: invoice / monthly / wallet (card handled by PaymentSection)
  const handleCheckout = async (method) => {
    setIsProcessing(true);
    setError(null);
    try {
      if (fulfillmentMethod === 'delivery') {
        if (!selectedAddressId) {
          throw new Error(t('selectAddressError', 'Please select a delivery address.'));
        }
        if (selectedDeliveryMessage) {
          throw new Error(selectedDeliveryMessage);
        }
      }
      if (fulfillmentMethod === 'pickup' && (!pickupLocationId || !pickupTime || !pickupDate)) {
        throw new Error(t('selectPickupError', 'Please select a pickup location, time window and date.'));
      }
      if (!selectedBillingAddressId) {
        throw new Error(t('selectBillingAddressError', 'Please select a billing address.'));
      }

      const payload = buildPayload();

      if (method === 'invoice') {
        const data = await checkoutService.createInvoiceOrder(payload);
        if (data.error) throw new Error(data.error);
        if (!variantSku) await clearCart();
        navigate(`/checkout/success?orderId=${data.orderId}&type=invoice`);
      } else if (method === 'monthly') {
        const data = await checkoutService.createMonthlyOrder(payload);
        if (data.error) throw new Error(data.error);
        if (!variantSku) await clearCart();
        navigate(`/checkout/success?orderId=${data.orderId}&type=monthly`);
      } else if (method === 'wallet') {
        const data = await checkoutService.createCheckoutSession(payload);
        if (data.error) throw new Error(data.error);
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    t, isAuthenticated, navigate,
    // Addresses
    addresses, billingAddresses, selectedAddressId, setSelectedAddressId,
    selectedBillingAddressId, setSelectedBillingAddressId,
    showAddressForm, setShowAddressForm, addressForm, setAddressForm,
    savingAddress, addressError, setAddressError, handleAddressSelect, handleAddressChange, handleAddAddress,
    deliveryUnavailableIds, selectedDeliveryMessage,
    showBillingAddressForm, setShowBillingAddressForm, billingAddressForm, setBillingAddressForm,
    savingBillingAddress, billingAddressError, handleBillingAddressSelect, handleAddBillingAddress,
    sameAsShippingAddress, toggleSameAsShipping, selectedShippingAddress: addresses.find(a => a.id === selectedAddressId) || null,
    // Pickup
    locations, holidays, pickupLocationId, setPickupLocationId, pickupTime, setPickupTime, pickupDate, setPickupDate,
    // Order
    items, summary, couponCode, setCouponCode, couponFeedbackKey,
    appliedCouponCodes, isApplyingCoupon,
    billingStatus, volumeDiscount, appliedBalance,
    fulfillmentMethod, setFulfillmentMethod,
    // UI
    isProcessing, setIsProcessing, error, setError,
    termsAccepted, setTermsAccepted, pricingAcknowledged, setPricingAcknowledged,
    marketingOptIn, setMarketingOptIn,
    // Actions
    handleApplyCoupon, handleCheckout, buildPayload, variantSku, quantity, cartItems, clearCart,
  };
}
